import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@mobiscroll/react-latest'
import moment from 'moment'
import SectionHeaderAndProgress from 'src/components/riders/rider-portal/trips/create/SectionHeaderAndProgress'
import MapModal from 'src/components/riders/rider-portal/trips/create/MapModal'
import PaymentSourceModal from 'src/components/riders/rider-portal/trips/create/PaymentSourceModal'
import ServiceAndPickup from 'src/components/riders/rider-portal/trips/create/Step1/ServiceAndPickup'
import TripDetails from 'src/components/riders/rider-portal/trips/create/Step2/TripDetails'
import ConfirmAndPay from 'src/components/riders/rider-portal/trips/create/Step3/ConfirmAndPay'
import SuccessBookingConfirmation from 'src/components/riders/rider-portal/trips/create/Step4/SuccessBookingConfirmation'
import { notification } from 'src/components/riders/rider-portal/common/Notification'
import ArrowRight2 from 'src/components/icons/ui/ArrowRight_2'
import { googleMapService } from 'src/services/GoogleMap'
import { useBreakpoint, useCurrentLocation } from 'src/components/riders/rider-portal/hooks'
import { handleRedirect, scrollToTop } from 'src/utils'
import { LOCAL_STORAGE_RIDER_PORTAL_CURRENT_USER_KEY } from 'src/utils/auth'
import { clearZoneCaches, isInsideCircle, isInsidePolygon } from 'src/utils/location'
import { IActiveTripResponseV2, ILocationV2, IPortalProduct, ISavedLocations, IService } from 'src/types'
import { ICreateTripBooking, ICreateTripProps, IPortalPassenger } from 'src/types/rider-portal'
import { RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'

interface ITripValues {
  accessibilities: string[]
  dateTime: {
    date: Date | string
    time: Date | string
  }
  locations: {
    dropOff: ILocationV2 | null
    pickUp: ILocationV2 | null
  }
  paymentSource?: string
  product: IPortalProduct | null
  service: IService | null
  specialInstructions: string
  tripRequestRiders: IPortalPassenger[]
}

const CREATE_TRIP_TOTAL_STEPS = 4
export const MAX_PASSENGER_COUNT = 6
const REDIRECT_TIMER = 10

const CreateTrip = ({
  availableProducts,
  availableServices,
  designatedServiceZones,
  fetchingProducts,
  fetchingSavedLocations,
  fetchingServices,
  isLoading,
  loggedInRiderDetails,
  requestingTripBooking,
  riderSavedLocations,
  serviceZones,
  createTripBooking,
  getAvailableProducts,
  getCurrentUserInfo,
  getRiderSavedLocations,
  getServicesByCurrentLocation,
  getServiceDesignatedZones,
  getServiceZones
}: ICreateTripProps) => {
  const [bookingType, setBookingType] = useState<'on-demand' | 'schedule'>('on-demand')
  const [center, setCenter] = useState<google.maps.LatLngLiteral | null>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [openMapModal, setOpenMapModal] = useState(false)
  const [redirectTimer, setRedirectTimer] = useState(REDIRECT_TIMER)
  const [showPaymentSourceModal, setShowPaymentSourceModal] = useState(false)
  const [successBookingDetails, setSuccessBookingDetails] = useState<IActiveTripResponseV2 | null>(null)
  const [tripValues, setTripValues] = useState<ITripValues>({
    accessibilities: [],
    dateTime: {
      date: '',
      time: ''
    },
    locations: {
      dropOff: null,
      pickUp: null
    },
    product: null,
    service: null,
    specialInstructions: '',
    tripRequestRiders: []
  })
  const redirectIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const {
    accessibilities,
    dateTime,
    locations,
    paymentSource,
    product,
    service,
    specialInstructions,
    tripRequestRiders
  } = tripValues
  const { coordinates, error } = useCurrentLocation()
  const device = useBreakpoint()
  const riderDetails = localStorage.getItem(LOCAL_STORAGE_RIDER_PORTAL_CURRENT_USER_KEY) || ''
  const {
    countryCode = '',
    email = '',
    firstName = '',
    id = '',
    lastName = '',
    organizationId = '',
    phone = ''
  } = JSON.parse(riderDetails)
  const riderPaymentSource =
    (loggedInRiderDetails && loggedInRiderDetails.wallet && loggedInRiderDetails.wallet.sources) || []
  const locationType = currentStep === 1 ? 'pickUp' : 'dropOff'
  const { common, dataFeedback, trips } = RIDER_PORTAL_UI_MESSAGES
  const STEP_HEADER_TITLE = [trips.serviceAndPickup, trips.tripDetails, trips.confirmAndPay]

  useEffect(() => {
    if (window) {
      scrollToTop()
    }
  }, [currentStep])

  // Redirect user to trip details page after successful booking confirmation
  useEffect(() => {
    if (currentStep === CREATE_TRIP_TOTAL_STEPS && successBookingDetails !== null) {
      redirectIntervalRef.current = setInterval(
        () => setRedirectTimer((prev: number) => (prev > 1 ? prev - 1 : 0)),
        1000
      )
    }

    return () => {
      if (redirectIntervalRef.current) {
        clearInterval(redirectIntervalRef.current)
      }
    }
  }, [currentStep, successBookingDetails])

  // Redirect if user did not click Book another rider
  useEffect(() => {
    if (redirectTimer === 0 && successBookingDetails !== null) {
      handleRedirect(`/rider-portal/booking/${successBookingDetails.id}`)
    }
  }, [redirectTimer])

  // Fetch users saved locations
  useEffect(() => {
    if (id && riderSavedLocations.length === 0) {
      getRiderSavedLocations(id)
    }
  }, [])

  // Fetch available services based on users current location
  useEffect(() => {
    if (coordinates && !error) {
      setCenter(coordinates)
      if (organizationId && availableServices.length === 0) {
        getServicesByCurrentLocation(coordinates, organizationId, 'active')
      }

      setCurrentLocationAsPickup()
    }
  }, [coordinates])

  // Fetch service zones and designated zones when user selects service
  useEffect(() => {
    if (service && service.id) {
      // Send request only if there is an organization id and when there is no zones data for the selected service(prevent extra api calls)
      if (organizationId && !serviceZones[service.id as keyof typeof serviceZones]) {
        getServiceZones(organizationId, service.id)
      }
      // Send request only if there is no designated zones data for the selected service(prevent extra api calls)
      if (!designatedServiceZones[service.id as keyof typeof designatedServiceZones]) {
        getServiceDesignatedZones(service.id)
      }
    }

    // Reset tripRequestRiders(each service has specific rider types) and product when user reselects other service
    setTripValues((prev: ITripValues) => ({
      ...prev,
      product: null,
      tripRequestRiders: []
    }))

    // Reset service zones cache(map) everytime user's select a service to avoid unbounded growth on long sessions
    clearZoneCaches()
  }, [service])

  // Automatically select the first service
  useEffect(() => {
    if (availableServices.length > 0) {
      handleSelectService(availableServices[0])
    }
  }, [availableServices])

  // Fetch available products whenever user selects service and if pickUp/dropOff locations has value
  useEffect(() => {
    if (
      locations.dropOff &&
      locations.dropOff.latitude &&
      locations.dropOff.longitude &&
      (locations.pickUp && locations.pickUp.latitude && locations.pickUp.longitude)
    ) {
      const { fullAddress, latitude, longitude, placeName } = locations.pickUp
      const {
        fullAddress: DOfullAddress,
        latitude: DOlatitude,
        longitude: DOlongitude,
        placeName: DOplaceName
      } = locations.dropOff
      const payload = {
        origin: {
          address: fullAddress,
          lat: latitude,
          lng: longitude,
          placeName
        },
        destination: {
          address: DOfullAddress,
          lat: DOlatitude,
          lng: DOlongitude,
          placeName: DOplaceName
        },
        displayPublicService: true,
        orgId: organizationId,
        serviceId: (service && service.id) || '',
        timezone: moment.tz.guess()
      }

      getAvailableProducts(payload, id)

      // Reset selected product when regenerating available products after update on service or locations data
      setTripValues((prev: ITripValues) => ({
        ...prev,
        product: null
      }))
    }
  }, [locations.dropOff, locations.pickUp, service])

  useEffect(() => {
    if (tripRequestRiders.length > 0) {
      // Update the product fare and price everytime rider is added or deleted
      if (product) {
        const selectedProduct = availableProducts.filter(prod => prod.id === product.id)[0]
        calculateFareAndPrice(selectedProduct)
      }

      // Update the trip values to delete paymentSource when tripRequest riders does not includes rider with Credit Card as fareType
      const isPaymentCreditCard = checkCreditCardPaymentMethod()
      if (!isPaymentCreditCard) {
        setTripValues(prev => {
          const updatedValue = { ...prev }
          delete updatedValue.paymentSource
          return { ...updatedValue }
        })
      }
    }
  }, [tripRequestRiders])

  const resetTripValues = () => {
    if (redirectIntervalRef.current) {
      clearInterval(redirectIntervalRef.current)
    }

    setTripValues({
      accessibilities: [],
      dateTime: {
        date: '',
        time: ''
      },
      locations: {
        dropOff: null,
        pickUp: null
      },
      product: null,
      service: availableServices[0],
      specialInstructions: '',
      tripRequestRiders: []
    })
    setCurrentLocationAsPickup()
    setSuccessBookingDetails(null)
    setRedirectTimer(REDIRECT_TIMER)
  }

  // Set user current location as pickup location
  const setCurrentLocationAsPickup = () => {
    if (coordinates && !error) {
      googleMapService
        .reverseGeocode(coordinates)
        .then((data: { formatted_address: string }) => {
          if (data.formatted_address) {
            setTripValues((prev: ITripValues) => ({
              ...prev,
              locations: {
                ...prev.locations,
                pickUp: {
                  fullAddress: data.formatted_address,
                  latitude: coordinates.lat,
                  longitude: coordinates.lng,
                  placeName: ''
                }
              }
            }))
          }
        })
        .catch(() =>
          notification({ color: 'danger', message: 'Error fetching place name of user current coordinates.' })
        )
    } else {
      notification({ color: 'danger', message: dataFeedback.pleaseAllowToUseLocation })
    }
  }

  const handleBookingTypeChange = (type: 'on-demand' | 'schedule') => {
    if (type === 'on-demand') {
      // Check if date and time has value then reset
      if (dateTime.date || dateTime.time) {
        setTripValues((prev: ITripValues) => ({
          ...prev,
          dateTime: { date: '', time: '' }
        }))
      }
      setBookingType(type)
    } else {
      setBookingType(type)
    }
  }

  const checkCreditCardPaymentMethod = () => {
    return tripRequestRiders.map(({ fareType }) => fareType).includes('Credit Card')
  }

  // Handles validation on all steps
  const handleStepsValidation = () => {
    if (currentStep !== 3) {
      const { dropOff, pickUp } = locations
      const isPaymentCreditCard = checkCreditCardPaymentMethod()
      switch (currentStep) {
        case 1:
          return service !== null && (pickUp !== null && pickUp.fullAddress && pickUp.latitude && pickUp.longitude)
        case 2:
          return (
            dropOff !== null &&
            (bookingType !== 'schedule' || (dateTime.date && dateTime.time)) &&
            product !== null &&
            tripRequestRiders.length !== 0 &&
            !(isPaymentCreditCard && !paymentSource)
          )
        default:
          return false
      }
    }

    // Disable the Book Ride button when user is on Trip Summary and updated the booking type to Schedule
    if (bookingType === 'schedule' && !(dateTime.date && dateTime.time)) {
      return false
    }
    return true
  }

  // Validate selected locations if it lies inside selected service zones
  const validateLocationCoordinates = (locationCoordinates: google.maps.LatLngLiteral) => {
    if (service && service.id && serviceZones[service.id]) {
      const CIRCLE_ZONE_VALIDATION = serviceZones[service.id]
        .filter((zone: { shapeType: string }) => zone.shapeType === 'shape_circle')
        .map(({ center: { coordinates }, radius }) => {
          const circleData = {
            center: { lat: coordinates[1], lng: coordinates[0] },
            radius
          }
          const isWithinZone = isInsideCircle(locationCoordinates, circleData)
          return isWithinZone
        })

      const POLYGON_ZONE_VALIDATION = serviceZones[service.id]
        .filter((zone: { shapeType: string }) => zone.shapeType === 'shape_multipolygon')
        .map(({ shape: { coordinates } }) => {
          const isWithinZone = isInsidePolygon(locationCoordinates, coordinates[0][0])
          return isWithinZone
        })

      const combinedZoneValidation = [...CIRCLE_ZONE_VALIDATION, ...POLYGON_ZONE_VALIDATION]
      return combinedZoneValidation.includes(true)
    }
    return false
  }

  // Save pickup/dropoff locations to the state when it is valid(occurs when selecting location from the MapModal and selecting location from Recent Locations)
  const handleLocationValues = (location: ILocationV2) => {
    const { latitude, longitude } = location
    const isWithinServiceZone = validateLocationCoordinates({ lat: latitude, lng: longitude })
    isWithinServiceZone
      ? setTripValues((prev: ITripValues) => ({
          ...prev,
          locations: {
            ...prev.locations,
            [locationType]: location
          }
        }))
      : notification({ color: 'danger', message: dataFeedback.locationOutOfServiceZone })
  }

  // Update fullAddress of the location(occurs when manually updating the pickup/dropoff addresses)
  const handleChangeAddress = (fullAddress: string) => {
    setTripValues((prev: ITripValues) => ({
      ...prev,
      locations: {
        ...prev.locations,
        [locationType]: {
          ...prev.locations[locationType],
          fullAddress
        }
      }
    }))
  }

  // Update location (occurs when marker is dragged in MapModal)
  const updateLocation = (location: ILocationV2, type: 'dropOff' | 'pickUp') => {
    const { fullAddress, latitude, longitude, placeName } = location
    const addressName = fullAddress ? fullAddress : locations[type] && locations[type]!.fullAddress

    const isWithinServiceZone = validateLocationCoordinates({ lat: latitude, lng: longitude })
    isWithinServiceZone
      ? setTripValues((prev: ITripValues) => ({
          ...prev,
          locations: {
            ...prev.locations,
            [type]: {
              fullAddress: addressName,
              latitude,
              longitude,
              placeName
            }
          }
        }))
      : notification({ color: 'danger', message: dataFeedback.locationOutOfServiceZone })
  }

  const handleSelectRecentLocation = (location: ISavedLocations) => {
    if (location.id) {
      const { coordinates, fullAddress, placeName } = location
      const locationDetails = {
        fullAddress,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        placeName
      }
      handleLocationValues(locationDetails)
    }
  }

  const calculateFareAndPrice = (product: IPortalProduct) => {
    const { fare, price } = product
    const chargeableRiderCount = tripRequestRiders
      .filter(rider => rider.fareType !== 'Free')
      .reduce((acc, rider) => acc + rider.totalRider, 0)

    setTripValues(prev => ({
      ...prev,
      product: {
        ...product,
        fare: fare * chargeableRiderCount,
        price: price * chargeableRiderCount
      }
    }))
  }

  const handleSelectProduct = (product: IPortalProduct) => {
    tripRequestRiders.length > 0
      ? calculateFareAndPrice(product) // If there is existing passenger details update the fare and price of the product based on the totalRider
      : setTripValues((prev: ITripValues) => ({ ...prev, product }))
  }

  const handleSelectService = (service: IService) => {
    setTripValues((prev: ITripValues) => ({
      ...prev,
      service
    }))
  }

  const handleSelectSource = (sourceId: string) => {
    setTripValues((prev: ITripValues) => ({
      ...prev,
      paymentSource: sourceId
    }))
  }

  const handleTripRiders = (passenger: IPortalPassenger) => {
    if (passenger.fareType === 'Credit Card' && !paymentSource) {
      setShowPaymentSourceModal(true)
    }

    const existingIndex = tripRequestRiders.findIndex(
      ({ riderType }: IPortalPassenger) => riderType === passenger.riderType
    )

    // If passenger types already exist add the rider count only.
    if (existingIndex >= 0) {
      const updated = tripRequestRiders.map((rider, index) => {
        return index === existingIndex ? { ...rider, totalRider: rider.totalRider + passenger.totalRider } : rider
      })
      return setTripValues((prev: ITripValues) => ({
        ...prev,
        tripRequestRiders: updated
      }))
    }

    setTripValues((prev: ITripValues) => ({
      ...prev,
      tripRequestRiders: [...prev.tripRequestRiders, passenger]
    }))
  }

  const updatePassengerCount = (passengerDetails: IPortalPassenger[]) => {
    setTripValues((prev: ITripValues) => ({
      ...prev,
      tripRequestRiders: [...passengerDetails]
    }))
  }

  const handleUpdatePassengerCount = ({ type, index }: { type: string; index: number }) => {
    const passengers = [...tripRequestRiders]
    let totalPassengerCount = 0
    passengers.forEach(({ totalRider }) => {
      totalPassengerCount += totalRider
    })

    // For subtracting of passenger (- sign)
    if (type === 'decrease') {
      // If current rider count is greater than 0 subtract, if it is already 0 remove from tripRequestRiders
      if (passengers[index].totalRider > 1) {
        const updated = passengers.map((rider, idx) => {
          return idx === index ? { ...rider, totalRider: rider.totalRider - 1 } : rider
        })
        updatePassengerCount(updated)
      } else {
        const filteredArr = tripRequestRiders.filter((riders: IPortalPassenger, idx: number) => idx !== index)
        updatePassengerCount(filteredArr)
      }
    } else {
      // For adding of passenger (+ sign)
      if (totalPassengerCount < MAX_PASSENGER_COUNT) {
        const updated = passengers.map((rider, idx) => {
          return idx === index ? { ...rider, totalRider: rider.totalRider + 1 } : rider
        })
        updatePassengerCount(updated)
      }
    }
  }

  const handleSelectAccessibilities = (checked: boolean, value: string) => {
    if (checked) {
      setTripValues((prev: ITripValues) => ({
        ...prev,
        accessibilities: [...prev.accessibilities, value]
      }))
    } else {
      const filteredAccessibilities = accessibilities.filter((note: string) => note !== value)
      setTripValues((prev: ITripValues) => ({
        ...prev,
        accessibilities: filteredAccessibilities
      }))
    }
  }

  const handleSpecialInstructionsChange = (value: string) => {
    setTripValues((prev: ITripValues) => ({
      ...prev,
      specialInstructions: value
    }))
  }

  const handleDateTimeChange = (type: 'date' | 'time', value: Date) => {
    setTripValues((prev: ITripValues) => ({
      ...prev,
      dateTime: {
        ...prev.dateTime,
        [type]: value
      }
    }))
  }

  const getTotalRiders = () => {
    let count = 0
    if (tripRequestRiders.length > 0) {
      tripRequestRiders.forEach(({ totalRider }) => (count += totalRider))
    }
    return count
  }

  const getTripNotes = () => {
    let initNotes = ''
    if (accessibilities.length > 0) {
      initNotes = accessibilities.join('; ')
    }
    if (specialInstructions) {
      const specialInst = initNotes ? `${initNotes}; ${specialInstructions}` : specialInstructions
      initNotes = specialInst
    }
    return initNotes
  }

  const getscheduledPickupTime = () => {
    const completeDateAndTime = moment(dateTime.date).set({
      hour: moment(dateTime.time, 'HH:mm a').hour(),
      minute: moment(dateTime.time, 'HH:mm a').minute()
    })
    return completeDateAndTime.toISOString()
  }

  const getRiderInfo = useCallback(() => {
    if (showPaymentSourceModal && checkCreditCardPaymentMethod()) {
      getCurrentUserInfo(id)
    }
  }, [id, showPaymentSourceModal, tripRequestRiders, getCurrentUserInfo])

  const handleContinueBtnClick = () => {
    if (currentStep === 2) {
      // Reset service zones cache(map) when user continues to step 3(trip summary) to avoid unbounded growth on long sessions
      clearZoneCaches()
    }

    if (currentStep !== 3) {
      setCurrentStep(prev => prev + 1)
    } else {
      if (!product || !service || !locations.dropOff || !locations.pickUp || !tripRequestRiders.length) {
        return
      }

      // Request trip booking
      const { distance, duration, id: productId, fare, name, price, pricingId } = product
      const { id: serviceId, name: serviceName } = service
      const payload: ICreateTripBooking = {
        additionalRiders: getTotalRiders(),
        distance: 0,
        dropoffLocation: locations.dropOff,
        duration: 0,
        estimates: {
          distance,
          duration,
          eta: 0,
          fare,
          price
        },
        notes: getTripNotes(),
        organizationId,
        passengerDetails: {
          attendant: false,
          disabilityApproved: false,
          tripRequestRiders: [...tripRequestRiders]
        },
        paymentMode: checkCreditCardPaymentMethod() ? 'Credit Card' : 'Wallet',
        pickupLocation: locations.pickUp,
        product: {
          fare,
          name,
          pricingId,
          productId,
          shouldDispatch: true
        },
        rider: {
          countryCode,
          email,
          firstName,
          lastName,
          phoneNumber: phone,
          riderId: id
        },
        ...(bookingType === 'schedule' ? { scheduledPickupTime: getscheduledPickupTime() } : {}),
        ...(checkCreditCardPaymentMethod() ? { paymentSource } : {}),
        service: { name: serviceName, serviceId }
      }
      createTripBooking(payload).then((data: IActiveTripResponseV2) => {
        if (data.id) {
          setCurrentStep(4)
          setSuccessBookingDetails(data)
        } else {
          notification({ color: 'danger', message: 'Booking failed. Please check the logs for more details.' })
        }
      })
    }
  }

  const bookAnotherRide = () => {
    resetTripValues()
    setCurrentStep(1)
  }

  return (
    <React.Fragment>
      <MapModal
        center={center}
        dropOff={locations.dropOff}
        isOpen={openMapModal}
        loading={loading}
        pickUp={locations.pickUp}
        serviceZones={service && service.id ? serviceZones[service.id as keyof typeof serviceZones] : []}
        title={trips.selectLocation}
        handleCloseModal={() => setOpenMapModal(false)}
        handleMapModalValues={handleLocationValues}
        setLoading={param => setLoading(param)}
        updateLocation={updateLocation}
      />
      <PaymentSourceModal
        isOpen={showPaymentSourceModal}
        loading={isLoading}
        paymentSource={paymentSource}
        riderPaymentSource={riderPaymentSource}
        getCurrentUserInfo={getRiderInfo}
        handleSelectSource={handleSelectSource}
        onClose={() => setShowPaymentSourceModal(false)}
      />
      <div className='bg-grey9 rp-create-trip-main-container mbsc-grid'>
        <div className='items-center justify-center mbsc-row'>
          <div className='mbsc-col-12 rp-create-trip-column'>
            <div
              className={`b-rad-12 bg-white rp-create-trip-wrapper ${
                requestingTripBooking ? 'pointer-events-none' : ''
              }`}
            >
              <div
                className={`bg-green1 flex items-center justify-between rp-create-trip-header ${
                  device === 'sm' ? 'flex-column row-gap-16' : ''
                }`}
              >
                <h2 className='font-white my0 text-xl weight-700'>{trips.bookYourRide}</h2>
                {currentStep !== CREATE_TRIP_TOTAL_STEPS && (
                  <div className='cursor--pointer flex'>
                    <div
                      className={`font-medium rp-trip-type-on-demand px2 py1 text-sm ${
                        bookingType === 'on-demand' ? 'type-active' : 'type-inactive'
                      }`}
                      role='button'
                      tabIndex={0}
                      onClick={() => handleBookingTypeChange('on-demand')}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleBookingTypeChange('on-demand')
                        }
                      }}
                    >
                      {common.rideNow}
                    </div>
                    <div
                      className={`font-medium rp-trip-type-schedule px2 py1 text-sm ${
                        bookingType === 'schedule' ? 'type-active' : 'type-inactive'
                      }`}
                      role='button'
                      tabIndex={0}
                      onClick={() => handleBookingTypeChange('schedule')}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleBookingTypeChange('schedule')
                        }
                      }}
                    >
                      {common.schedule}
                    </div>
                  </div>
                )}
              </div>
              <div className='rp-create-trip-body'>
                {currentStep !== CREATE_TRIP_TOTAL_STEPS && (
                  <div className='rp-mb24'>
                    <SectionHeaderAndProgress
                      currentStep={currentStep}
                      steps={CREATE_TRIP_TOTAL_STEPS}
                      title={STEP_HEADER_TITLE[currentStep - 1]}
                    />
                  </div>
                )}
                {currentStep === 1 && (
                  <ServiceAndPickup
                    availableServices={availableServices}
                    fetchingProducts={fetchingProducts}
                    fetchingSavedLocations={fetchingSavedLocations}
                    fetchingServices={fetchingServices}
                    loading={loading}
                    pickUpLocation={locations.pickUp}
                    riderSavedLocations={riderSavedLocations}
                    selectedService={service}
                    handleChangeAddress={handleChangeAddress}
                    handleMapLabelClick={() => setOpenMapModal(true)}
                    handleSelectRecentLocation={handleSelectRecentLocation}
                    handleSelectService={handleSelectService}
                    setCurrentLocationAsPickup={setCurrentLocationAsPickup}
                  />
                )}
                {currentStep === 2 && (
                  <TripDetails
                    accessibilities={accessibilities}
                    availableProducts={availableProducts}
                    bookingType={bookingType}
                    dateTime={dateTime}
                    dropOffLocation={locations.dropOff}
                    fetchingProducts={fetchingProducts}
                    selectedProduct={product}
                    selectedService={service}
                    specialInstructions={specialInstructions}
                    tripRequestRiders={tripRequestRiders}
                    handleChangeAddress={handleChangeAddress}
                    handleDateTimeChange={handleDateTimeChange}
                    handleMapLabelClick={() => setOpenMapModal(true)}
                    handleSelectAccessibilities={handleSelectAccessibilities}
                    handleSelectProduct={handleSelectProduct}
                    handleShowPaymentSourceModal={() => setShowPaymentSourceModal(true)}
                    handleSpecialInstructionsChange={handleSpecialInstructionsChange}
                    handleTripRiders={handleTripRiders}
                    handleUpdatePassengerCount={handleUpdatePassengerCount}
                  />
                )}
                {currentStep === 3 && (
                  <ConfirmAndPay
                    accessibilities={accessibilities}
                    dateTime={dateTime}
                    locations={locations}
                    product={product}
                    service={service}
                    specialInstructions={specialInstructions}
                    tripRequestRiders={tripRequestRiders}
                    setCurrentStep={step => setCurrentStep(step)}
                  />
                )}
                {currentStep === 4 && <SuccessBookingConfirmation successBookingDetails={successBookingDetails} />}
                {currentStep !== CREATE_TRIP_TOTAL_STEPS ? (
                  <div className='column-gap-12 flex justify-end rp-mt32'>
                    {currentStep > 1 && (
                      <Button
                        className='rp-btn-back'
                        disabled={requestingTripBooking}
                        onClick={() => setCurrentStep(prev => prev - 1)}
                      >
                        {common.back}
                      </Button>
                    )}
                    <Button
                      className='rp-btn-continue'
                      disabled={!handleStepsValidation() || requestingTripBooking}
                      onClick={handleContinueBtnClick}
                    >
                      {requestingTripBooking ? common.processing : currentStep !== 3 ? common.continue : trips.bookRide}
                      {!requestingTripBooking && <ArrowRight2 className='icon--18x18 rp-ml4 svg-stroke-white' />}
                    </Button>
                  </div>
                ) : (
                  <div className='rp-mt32'>
                    <p className='font-black2 mt0 rp-mb16 text-center text-md'>{`${
                      dataFeedback.redirectNotification
                    } in ${redirectTimer}`}</p>
                    <p className='font-black2 text-center text-md'>{'OR'}</p>
                    <Button className='rp-btn-continue w100' onClick={bookAnotherRide}>
                      {trips.bookAnotherRide}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default CreateTrip



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/trips/create/CreateTrip.tsx